import { Button } from "@screencloud/screencloud-ui-components";
import { FormattedMessage } from "react-intl";
import { formatted } from "src/billinglatest/util/currency";
import darkPixiIcon from "src/pages/Welcome/images/dark-pixi-icon.svg";
import {
  emptyPixiAddress,
  type PixiWizardSelection,
} from "../../hooks/usePixiWizardState";
import { useCheckoutPricing } from "../CheckoutStep/useCheckoutPricing";
import { maskEmail } from "./utils/maskEmail";
import {
  CompleteButtonRow,
  CompleteLayout,
  Divider,
  FreeDeviceLabel,
  Header,
  Heading,
  LicenseValue,
  PixiInlineLogo,
  RenewSubtext,
  RowLabel,
  RowValue,
  SubTitle,
  SuccessIcon,
  SummaryCard,
  SummaryRow,
  TotalLabel,
  TotalValue,
} from "./styles";

export interface CompleteStepProps {
  state: PixiWizardSelection;
  /** Exits the wizard (closes the modal). */
  onComplete: () => void;
}

/**
 * The post-purchase success screen (GRWBKK-1663, redesigned in GRWBKK-1672).
 * Renders inside the wizard modal in place of Checkout once the purchase
 * succeeds, as a single "order summary" card. Values come from the shared
 * checkout pricing (repoint to the customer-create response when that call
 * lands).
 *
 * The free-device deal is itemised as two lines — the devices' original cost and
 * an equal "Free device offer" discount — so the breakdown sums to the total
 * rather than showing a bare "$0" (per the Slack decision on GRWBKK-1672).
 */
export const CompleteStep = ({ state, onComplete }: CompleteStepProps) => {
  const {
    planContent,
    currency,
    billingCycle,
    licenses,
    isDiscount,
    isFreeDevice,
    licencesCents,
    deviceSavingsCents,
    discountCents,
    shippingCents,
    grandTotalCents,
  } = useCheckoutPricing(state);

  const price = (cents: number) => formatted(currency, cents);
  const billingAddress = state.billingAddress ?? emptyPixiAddress();
  const maskedEmail = maskEmail(billingAddress.email);
  const isYearly = billingCycle === "year";

  return (
    <CompleteLayout data-testid="complete-step">
      <Header>
        <SuccessIcon
          name="checked-circle-fill"
          aria-hidden
          data-testid="complete-success-icon"
        />
        <Heading>
          <FormattedMessage
            id="pixi_wizard.complete.title"
            defaultMessage="Subscription Successful"
          />
        </Heading>
        <SubTitle data-testid="complete-subtitle">
          <FormattedMessage
            id="pixi_wizard.complete.subtitle"
            defaultMessage="Thank you for your subscription. Invoice and confirmation email has been sent to your email {email}"
            values={{ email: maskedEmail }}
          />
        </SubTitle>
      </Header>

      <SummaryCard data-testid="complete-summary">
        <SummaryRow>
          <RowLabel>
            <FormattedMessage
              id="pixi_wizard.complete.plan"
              defaultMessage="Plan"
            />
          </RowLabel>
          <RowValue data-testid="complete-plan">
            {planContent.name}
            {" ("}
            {isYearly ? (
              <FormattedMessage
                id="pixi_wizard.complete.plan_yearly"
                defaultMessage="yearly"
              />
            ) : (
              <FormattedMessage
                id="pixi_wizard.complete.plan_monthly"
                defaultMessage="monthly"
              />
            )}
            {")"}
          </RowValue>
        </SummaryRow>
        <Divider />

        <SummaryRow>
          <RowLabel>
            <FormattedMessage
              id="pixi_wizard.complete.licenses"
              defaultMessage="{count, plural, one {{count} License} other {{count} Licenses}}"
              values={{ count: licenses }}
            />
          </RowLabel>
          <LicenseValue>
            <RowValue data-testid="complete-licenses-price">
              {price(licencesCents)}
            </RowValue>
            <RenewSubtext data-testid="complete-renewal">
              {isYearly ? (
                <FormattedMessage
                  id="pixi_wizard.complete.renewed_yearly"
                  defaultMessage="Renewed every year"
                />
              ) : (
                <FormattedMessage
                  id="pixi_wizard.complete.renewed_monthly"
                  defaultMessage="Renewed every month"
                />
              )}
            </RenewSubtext>
          </LicenseValue>
        </SummaryRow>
        <Divider />

        {isFreeDevice && (
          <>
            <SummaryRow>
              <FreeDeviceLabel data-testid="complete-device-line">
                <FormattedMessage
                  id="pixi_wizard.complete.device_line"
                  defaultMessage="x{count} {pixiLogo} {count, plural, one {device} other {devices}}"
                  values={{
                    count: licenses,
                    pixiLogo: <PixiInlineLogo src={darkPixiIcon} alt="PIXI" />,
                  }}
                />
              </FreeDeviceLabel>
              <RowValue data-testid="complete-device-cost">
                {price(deviceSavingsCents)}
              </RowValue>
            </SummaryRow>
            <SummaryRow>
              <RowLabel>
                <FormattedMessage
                  id="pixi_wizard.complete.free_device_offer"
                  defaultMessage="Free device offer"
                />
              </RowLabel>
              <RowValue data-testid="complete-device-discount">
                -{price(deviceSavingsCents)}
              </RowValue>
            </SummaryRow>
            <Divider />
            <SummaryRow>
              <RowLabel>
                <FormattedMessage
                  id="pixi_wizard.complete.shipping"
                  defaultMessage="One time shipping cost"
                />
              </RowLabel>
              <RowValue data-testid="complete-shipping">
                {price(shippingCents)}
              </RowValue>
            </SummaryRow>
            <Divider />
          </>
        )}

        {isDiscount && (
          <>
            <SummaryRow>
              <RowLabel>
                <FormattedMessage
                  id="pixi_wizard.complete.discount"
                  defaultMessage="Discount"
                />
              </RowLabel>
              <RowValue data-testid="complete-discount">
                -{price(discountCents)}
              </RowValue>
            </SummaryRow>
            <Divider />
          </>
        )}

        <SummaryRow>
          <TotalLabel>
            <FormattedMessage
              id="pixi_wizard.complete.total"
              defaultMessage="Total paid today"
            />
          </TotalLabel>
          <TotalValue data-testid="complete-grand-total">
            {price(grandTotalCents)}
          </TotalValue>
        </SummaryRow>
      </SummaryCard>

      <CompleteButtonRow>
        <Button primary onClick={onComplete} data-testid="complete-button">
          <FormattedMessage
            id="pixi_wizard.complete.complete"
            defaultMessage="Complete"
          />
        </Button>
      </CompleteButtonRow>
    </CompleteLayout>
  );
};
